import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Layers, BarChart3, Calendar, FileText, Users, Shield,
  CheckCircle, ArrowRight, Menu, X, ChevronDown, Plus, Minus,
  Home, HardHat, Building2, TrendingUp, Maximize2,
  Mail, MessageSquare, HelpCircle, Heart, Video, Code, Wand2
} from 'lucide-react';

// ─── CSS keyframe for progress bar (avoids rAF-driven React re-renders) ───────
const PROGRESS_STYLE = document.createElement('style');
PROGRESS_STYLE.textContent = `@keyframes progressFill { from { width: 0%; } to { width: 100%; } }`;
if (!document.head.querySelector('[data-stratalogy-progress]')) {
  PROGRESS_STYLE.setAttribute('data-stratalogy-progress', '');
  document.head.appendChild(PROGRESS_STYLE);
}

// ─── Brand colors from logo ───────────────────────────────────────────────────
// Teal:  #359b95  |  Mid teal: #3b8c90  |  Navy: #1f425c  |  Dark navy: #243549

const FEATURES = [
  {
    icon: Layers,
    title: 'Never Forget Which Tile You Picked',
    desc: 'Track every material decision room by room — from your shortlist to what\'s ordered and installed. No more digging through texts to find that paint color.',
    screen: '/screenshots/screenshot-materials.png',
  },
  {
    icon: Calendar,
    title: 'Know If Your Contractor Is Behind',
    desc: 'See your entire project timeline at a glance — from Site Prep to Final Walkthrough. When milestones slip, you\'ll know before your contractor tells you.',
    screen: '/screenshots/screenshot-timeline.png',
  },
  {
    icon: BarChart3,
    title: 'Answer "Where Did That $8K Go?"',
    desc: 'Every invoice, receipt, and material cost logged against your budget in real time. When your spouse asks, you\'ll have the answer in two clicks.',
    screen: '/screenshots/screenshot-financial.png',
  },
  {
    icon: FileText,
    title: 'Prove Every Dollar at Resale',
    desc: 'Export a complete record of your renovation — materials, costs, timelines, and decisions — as a PDF or CSV. This is your proof of value when you sell.',
    screen: '/screenshots/screenshot-reports.png',
  },
  {
    icon: Shield,
    title: 'See Your Project\'s Vital Signs',
    desc: 'A live health score grades your project across 6 dimensions — so you always know what\'s on track and what needs attention, without being a construction expert.',
    screen: '/screenshots/screenshot-dashboard.png',
  },
  {
    icon: Users,
    title: 'One Place for Every Contractor',
    desc: 'Keep every contractor, supplier, and vendor organized — tied directly to the work they did and materials they supplied. No more lost business cards.',
    screen: '/screenshots/screenshot-contacts.png',
  },
];

const PAIN_POINTS = [
  {
    icon: '💸',
    stake: '$12,400',
    headline: 'Average cost overrun on home renovations',
    detail: 'Without real-time budget tracking, expenses pile up invisibly. Stratalogy logs every invoice, receipt, and material cost so you see exactly where every dollar goes — before it\u2019s too late.',
  },
  {
    icon: '📋',
    stake: '63%',
    headline: 'of homeowners lose track of project decisions',
    detail: 'Scattered texts, emails, and sticky notes mean lost choices and repeated work. One organized platform keeps every material, room, and vendor decision in one place.',
  },
  {
    icon: '📅',
    stake: '2–4 months',
    headline: 'is the average renovation delay',
    detail: 'Missed milestones cascade. Step-by-step timeline tracking keeps your project moving from Site Prep to Final Walkthrough — on schedule.',
  },
  {
    icon: '🏠',
    stake: '$0',
    headline: 'is what you can prove you invested at resale',
    detail: 'No documentation = no proof of value. Stratalogy gives you a permanent, exportable property record — materials, costs, timelines — that proves every dollar you put in.',
  },
];

const FAQ_ITEMS = [
  {
    q: "Isn\u2019t tracking my renovation my contractor\u2019s job?",
    a: "Your contractor manages the build \u2014 but you\u2019re the one writing the checks. Stratalogy gives you independent visibility into what was ordered, what was spent, and what\u2019s on schedule. Think of it as the homeowner\u2019s side of the ledger. When your contractor says \u201cwe\u2019re on budget,\u201d you\u2019ll know for certain \u2014 because you have the receipts, timelines, and material decisions in one place.",
  },
  {
    q: "Why not just use a spreadsheet or free AI tool?",
    a: "You absolutely can \u2014 and most people start there. The problem isn\u2019t the first week. It\u2019s month three, when you have 40 invoices, 6 contractors, 200 material decisions, and a spouse asking \u201cwhere did that $8,000 go?\u201d Spreadsheets don\u2019t link your receipts to your rooms, track milestones against your budget, or give you an exportable record at resale. Stratalogy was purpose-built for this.",
  },
  {
    q: "My project is already underway \u2014 is it too late to start?",
    a: "Most users join mid-project. You can upload existing documents, invoices, and photos in bulk, and our system helps organize them into the right categories. Even capturing just the second half of your build gives you a valuable record you wouldn\u2019t have otherwise.",
  },
  {
    q: "What counts as one \u2018project\u2019?",
    a: "One project = one property. If you\u2019re redoing your kitchen, replacing flooring, and updating a fireplace in the same house, that\u2019s all one project. You organize it by room inside the platform. You\u2019d only need a second project if you\u2019re managing a completely separate property \u2014 like a rental or investment home.",
  },
  {
    q: "What happens to my data when my project is finished?",
    a: "Your data stays in your account for as long as it\u2019s active \u2014 even after the renovation is done. Many homeowners keep their account specifically for the resale record. You can also export everything (materials, costs, timelines, documents) as a PDF or CSV at any time. If you cancel, your data remains accessible through the end of your billing period, and you can export it all before then. We\u2019ll never delete your data without warning you first.",
  },
  {
    q: "Is $39/month worth it for a home renovation?",
    a: "The average renovation goes $12,400 over budget. A single untracked contractor change order can cost thousands. Stratalogy costs less than one lunch with your GC per month \u2014 and it\u2019s the only tool that gives you a permanent, exportable record of every dollar and decision. If it saves you from even one missed invoice or forgotten material choice, it\u2019s paid for itself many times over.",
  },
  {
    q: "Can I cancel at any time?",
    a: "Yes. No contracts, no cancellation fees. You can cancel from your account settings in two clicks, and your data stays accessible through the end of your billing period.",
  },
];

// ─── Nav ──────────────────────────────────────────────────────────────────────
function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 shadow-sm" style={{borderTop: '3px solid #359b95', willChange: 'transform'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-18 flex items-center justify-between" style={{height:'72px'}}>
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img src="/stratalogy-logo-transparent.png" alt="Stratalogy" className="h-12 w-auto" />
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-slate-600 hover:text-[#1f425c] text-sm font-medium transition-colors">Features</a>
          <a href="#how-it-works" className="text-slate-600 hover:text-[#1f425c] text-sm font-medium transition-colors">How It Works</a>
          <a href="#pricing" className="text-slate-600 hover:text-[#1f425c] text-sm font-medium transition-colors">Pricing</a>
          <a href="#faq" className="text-slate-600 hover:text-[#1f425c] text-sm font-medium transition-colors">FAQ</a>
          <Link to="/login" className="text-slate-600 hover:text-[#1f425c] text-sm font-medium transition-colors">Sign In</Link>
          <Link
            to="/register"
            className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-opacity duration-200 hover:opacity-90"
            style={{background:'linear-gradient(135deg, #359b95, #1f425c)'}}
          >
            Start Free Trial
          </Link>
        </div>

        {/* Mobile menu button */}
        <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-3 shadow-lg">
          <a href="#features" className="block text-slate-700 hover:text-[#1f425c] text-sm font-medium py-2" onClick={() => setOpen(false)}>Features</a>
          <a href="#how-it-works" className="block text-slate-700 hover:text-[#1f425c] text-sm font-medium py-2" onClick={() => setOpen(false)}>How It Works</a>
          <a href="#pricing" className="block text-slate-700 hover:text-[#1f425c] text-sm font-medium py-2" onClick={() => setOpen(false)}>Pricing</a>
          <a href="#faq" className="block text-slate-700 hover:text-[#1f425c] text-sm font-medium py-2" onClick={() => setOpen(false)}>FAQ</a>
          <Link to="/login" className="block text-slate-700 hover:text-[#1f425c] text-sm font-medium py-2" onClick={() => setOpen(false)}>Sign In</Link>
          <Link
            to="/register"
            className="block text-white text-sm font-semibold px-5 py-2.5 rounded-lg text-center transition-opacity duration-200 hover:opacity-90"
            style={{background:'linear-gradient(135deg, #359b95, #1f425c)'}}
            onClick={() => setOpen(false)}
          >
            Start Free Trial
          </Link>
        </div>
      )}
    </nav>
  );
}

// ─── Hero — left-aligned value proposition + right-side lifestyle image ────────
function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden bg-white" style={{ paddingTop: '96px' }}>
      {/* Top gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #359b95, #1f425c)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">

          {/* ── Left column: text content ──────────────────────────────── */}
          <div className="text-left">
            {/* "Built by a homeowner" — LARGE and prominent */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}>
                <Heart className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold" style={{ color: '#359b95' }}>
                Built by a Homeowner, for Homeowners
              </span>
            </div>

            {/* Main headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4" style={{ color: '#1f425c' }}>
              Stop losing money on your renovation.{' '}
              <span style={{ color: '#359b95' }}>Start tracking every dollar and decision.</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base sm:text-lg mb-3 leading-relaxed" style={{ color: '#243549' }}>
              The all-in-one platform that tracks your budget, materials, timeline, and contractors — so you finish on time, on budget, and with proof of every improvement at resale.
            </p>

            {/* Onboarding wizard callout */}
            <div className="flex items-start gap-3 mb-6 p-3 rounded-xl" style={{ background: '#f0fafa', border: '1px solid #b2dedd' }}>
              <Wand2 className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#359b95' }} />
              <p className="text-sm leading-relaxed" style={{ color: '#1f425c' }}>
                <span className="font-semibold">Set up in 2 minutes, not 2 weeks.</span> Our guided onboarding wizard walks you through everything — add your project, import documents, and start tracking immediately. No learning curve.
              </p>
            </div>

            {/* CTAs — side by side */}
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <button
                onClick={() => navigate('/register')}
                className="flex items-center justify-center gap-2 text-white font-semibold px-7 py-3.5 rounded-xl text-base transition-[opacity,transform] duration-200 hover:opacity-90 hover:scale-105 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)', boxShadow: '0 4px 20px rgba(53,155,149,0.35)' }}
              >
                Start Your Free Trial
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-2 font-semibold px-7 py-3.5 rounded-xl text-base border-2 transition-colors duration-200 hover:bg-slate-50"
                style={{ borderColor: '#1f425c', color: '#1f425c' }}
              >
                Sign In
              </button>
            </div>

            {/* No-card trial — prominent callout, not fine print */}
            <div className="flex items-center gap-3 mb-6 p-3 rounded-xl bg-white border border-slate-200 shadow-sm">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#359b95' }}>
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1f425c' }}>7-day free trial, card required. Cancel anytime before day 7 and you won't be charged.</p>
                <p className="text-xs text-slate-500">Cancel anytime before day 7 and you won't be charged.</p>
              </div>
            </div>

            {/* Scenario pills — compact, inline */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { icon: Home, label: 'Kitchen renovation' },
                { icon: HardHat, label: 'Addition or extension' },
                { icon: Building2, label: 'New construction' },
                { icon: TrendingUp, label: 'Investment property' },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium select-none"
                    style={{ background: '#f0fafa', borderColor: '#b2dedd', color: '#1f425c' }}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: '#359b95' }} />
                    {s.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Right column: lifestyle image — homeowner using platform ── */}
          <div className="relative">
            {/* Glow — box-shadow instead of blur filter for scroll perf */}
            <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(53,155,149,0.25), 0 2px 12px rgba(0,0,0,0.1)' }}>
              <img
                src="/images/hero-homeowner-platform.png"
                alt="Homeowner managing renovation from laptop — tracking budget, materials, and timeline in one place"
                className="w-full h-auto block object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="flex justify-center pb-6">
        <a href="#pain-points" className="text-slate-400 hover:text-[#359b95] transition-colors animate-bounce">
          <ChevronDown className="h-6 w-6" />
        </a>
      </div>
    </section>
  );
}

// ─── Pain Points — two-column layout with lifestyle image ─────────────────────
function PainPoints() {
  return (
    <section id="pain-points" className="py-16 px-4" style={{ background: '#f8fafc' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: lifestyle image of renovation chaos */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg order-2 lg:order-1">
            <img
              src="/images/kitchen-renovation.jpg"
              alt="Kitchen mid-renovation — the kind of chaos Stratalogy helps you manage"
              loading="lazy"
              className="w-full h-auto block object-cover"
              style={{ maxHeight: '500px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white text-sm font-medium drop-shadow-lg">
                Without a system, every renovation looks like this — disorganized, over budget, and behind schedule.
              </p>
            </div>
          </div>

          {/* Right: pain point stats */}
          <div className="order-1 lg:order-2">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-left" style={{ color: '#243549' }}>
              The real cost of &ldquo;winging it&rdquo;
            </h2>
            <p className="text-slate-500 text-base mb-8 text-left">
              Every renovation without a system is a gamble. Here's what homeowners lose.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PAIN_POINTS.map((p) => (
                <div key={p.headline} className="bg-white rounded-xl p-5 border border-slate-100 shadow-sm text-left">
                  <div className="text-2xl mb-2">{p.icon}</div>
                  <p className="text-xl font-extrabold mb-1" style={{ color: '#359b95' }}>{p.stake}</p>
                  <p className="text-xs font-semibold text-slate-700 mb-2">{p.headline}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{p.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Features (auto-cycling slideshow — screenshots shown ONCE, large) ────────
const CYCLE_MS = 5000;
const RESUME_MS = 8000;

function Features() {
  const [active, setActive]       = useState(0);
  const [paused, setPaused]       = useState(false);
  const [lightbox, setLightbox]   = useState(null);
  // CSS-transition progress bar: key flips on each cycle to restart the animation
  const [barKey, setBarKey]       = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cycleRef  = useRef(null);
  const resumeRef = useRef(null);

  const activeFeature = FEATURES[active];

  // Advance to next feature — no rAF, no per-frame React re-renders
  const advance = useCallback(() => {
    setActive(prev => (prev + 1) % FEATURES.length);
    setBarKey(k => k + 1);
  }, []);

  const startCycle = useCallback(() => {
    clearInterval(cycleRef.current);
    setBarKey(k => k + 1);
    cycleRef.current = setInterval(advance, CYCLE_MS);
  }, [advance]);

  const stopCycle = useCallback(() => {
    clearInterval(cycleRef.current);
  }, []);

  useEffect(() => {
    startCycle();
    return () => { stopCycle(); clearTimeout(resumeRef.current); };
  }, [startCycle, stopCycle]);

  const handleClick = (i) => {
    setActive(i);
    setPaused(true);
    stopCycle();
    setBarKey(k => k + 1);
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => {
      setPaused(false);
      startCycle();
    }, RESUME_MS);
  };

  return (
    <section id="features" className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section header — left aligned */}
        <div className="mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-left" style={{ color: '#243549' }}>
            Finally, answers instead of guesswork.
          </h2>
          <p className="text-slate-500 text-base max-w-2xl text-left">
            No construction experience needed. Stratalogy replaces the spreadsheets, texts, and sticky notes with one organized place for every decision, dollar, and deadline.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          {/* ── Feature list (left sidebar) ──────────────────────────── */}
          <div className="lg:w-1/3 space-y-2">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              const isActive = active === i;
              return (
                <button
                  key={f.title}
                  onClick={() => handleClick(i)}
                  className={`relative w-full text-left rounded-xl transition-[background-color,box-shadow] duration-200 flex items-start gap-4 overflow-hidden ${
                    isActive ? 'text-white shadow-lg' : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                  }`}
                  style={isActive ? { background: 'linear-gradient(135deg, #243549, #1f425c)' } : {}}
                >
                  <div className="flex-1 flex items-start gap-4 p-4">
                    <div className={`p-2 rounded-lg shrink-0 ${isActive ? 'bg-[#359b95]/30' : 'bg-white border border-slate-200'}`}>
                      <Icon className={`h-5 w-5 ${isActive ? 'text-[#5bbdb8]' : 'text-[#359b95]'}`} />
                    </div>
                    <div className="min-w-0">
                      <p className={`font-semibold text-sm mb-1 ${isActive ? 'text-white' : 'text-slate-800'}`}>{f.title}</p>
                      <p className={`text-xs leading-relaxed ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>{f.desc}</p>
                    </div>
                  </div>

                  {isActive && !paused && (
                    <div
                      key={barKey}
                      className="absolute bottom-0 left-0 h-0.5"
                      style={{
                        background: '#359b95',
                        width: '0%',
                        animation: `progressFill ${CYCLE_MS}ms linear forwards`,
                      }}
                    />
                  )}
                </button>
              );
            })}

            <div className="flex items-center justify-between pt-2 px-1">
              <div className="flex items-center gap-1.5">
                {FEATURES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleClick(i)}
                    className="rounded-full transition-[width] duration-300"
                    style={{
                      width:  active === i ? '20px' : '6px',
                      height: '6px',
                      background: active === i ? '#359b95' : '#cbd5e1',
                    }}
                    aria-label={`Go to feature ${i + 1}`}
                  />
                ))}
              </div>
              <span className="text-xs text-slate-400 select-none">
                {paused ? 'Paused — resuming shortly' : 'Auto-advancing'}
              </span>
            </div>
          </div>

          {/* ── Screenshot panel (right — large, crisp, click-to-enlarge) */}
          <div className="lg:w-2/3">
            {activeFeature.screen ? (
              <div className="relative group cursor-zoom-in" onClick={() => setLightbox(activeFeature.screen)}>
                {/* Glow — box-shadow instead of blur filter for scroll perf */}
                <div className="relative rounded-2xl overflow-hidden border border-slate-200" style={{ boxShadow: '0 6px 30px rgba(53,155,149,0.2), 0 2px 8px rgba(0,0,0,0.08)' }}>
                  <img
                    key={activeFeature.screen}
                    src={activeFeature.screen}
                    alt={activeFeature.title}
                    className="w-full h-auto block"
                    style={{ imageRendering: 'auto', maxWidth: '1024px' }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl"
                    style={{ background: 'rgba(31,66,92,0.35)' }}>
                    <div className="flex items-center gap-2 text-white text-sm font-semibold bg-white/30 px-4 py-2 rounded-full">
                      <Maximize2 className="h-4 w-4" />
                      Click to enlarge
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Lightbox overlay */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-8"
          style={{ background: 'rgba(15,30,42,0.92)' }}
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-6xl w-full" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setLightbox(null)}
              className="absolute -top-4 -right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-white shadow-lg transition-opacity hover:opacity-80"
              style={{ background: '#1f425c' }}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="rounded-2xl overflow-hidden shadow-2xl border-2" style={{ borderColor: '#359b95' }}>
              <img src={lightbox} alt="Screenshot enlarged" className="w-full h-auto block" />
            </div>
            <p className="text-center text-slate-400 text-xs mt-3">Press Esc or click outside to close</p>
          </div>
        </div>
      )}
    </section>
  );
}

// ─── How It Works — side-by-side with lifestyle image ─────────────────────────
function HowItWorks() {
  const steps = [
    { num: '01', title: 'Create your property', desc: 'Name it, set the budget, and your dashboard is ready. Takes under 2 minutes.' },
    { num: '02', title: 'Follow the onboarding wizard', desc: 'Our guided wizard walks you through adding rooms, materials, contractors, and documents — step by step.' },
    { num: '03', title: 'Track budget & timeline', desc: 'Log invoices, receipts, and milestones as the project progresses. Everything auto-organizes.' },
    { num: '04', title: 'Own the permanent record', desc: 'Export reports, share with contractors, and keep the full history of your property forever.' },
  ];

  return (
    <section id="how-it-works" className="py-16 px-4" style={{ background: '#f0fafa' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: steps */}
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-left" style={{ color: '#1f425c' }}>
              Set up in 2 minutes. Not 2 weeks.
            </h2>
            <p className="text-slate-500 text-base mb-8 text-left">
              No training needed. Our onboarding wizard guides you through every step — just answer a few questions and your project is organized.
            </p>

            <div className="space-y-5">
              {steps.map((s, i) => (
                <div key={s.num} className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold text-white shrink-0"
                    style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}>
                    {s.num}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-left" style={{ color: '#1f425c' }}>{s.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed text-left">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: lifestyle image showing homeowner + contractor */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            <img
              src="/images/contractor-meeting.jpg"
              alt="Homeowner discussing renovation plans with contractor"
              loading="lazy"
              className="w-full h-auto block object-cover"
              style={{ maxHeight: '520px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white text-sm font-medium drop-shadow-lg">
                Stay in control of every conversation with your contractor — because you have the data.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Onboarding Wizard Callout — dedicated section to counter "tedious" concern
function OnboardingCallout() {
  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: timeline screenshot showing organized project */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl" style={{ border: '2px solid #359b95' }}>
            <img
              src="/screenshots/screenshot-timeline.png"
              alt="Stratalogy timeline view showing organized project milestones"
              loading="lazy"
              className="w-full h-auto block"
              style={{ imageRendering: 'auto', maxWidth: '1024px' }}
            />
          </div>

          {/* Right: onboarding messaging */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#359b95' }}>
                <Wand2 className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-left" style={{ color: '#243549' }}>
                Not another tedious tool.
              </h2>
            </div>

            <p className="text-base text-slate-600 leading-relaxed mb-6 text-left">
              We know what you're thinking: <em>"Do I really have time to learn another platform?"</em> That's
              exactly why we built the onboarding wizard. It does the heavy lifting for you.
            </p>

            <div className="space-y-4">
              {[
                { title: 'Answer a few questions', desc: 'Project type, budget range, and rooms. That\'s it to get started.' },
                { title: 'Import what you have', desc: 'Upload existing invoices, receipts, photos, and documents in bulk. We organize them automatically.' },
                { title: 'Your dashboard is ready', desc: 'Materials tracked, timeline set, budget loaded. Start managing — not setting up.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-white text-xs font-bold" style={{ background: '#359b95' }}>
                    {i + 1}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-sm text-slate-800">{item.title}</p>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────
const PRICING_FEATURES = [
  'Full material & room tracking',
  'Timeline & milestone management',
  'Financial dashboard & budget tracking',
  'Invoice & receipt management',
  'Document storage & organization',
  'Project completion packet for resale',
  '1 project included · $9/mo each additional',
  'Export everything as PDF or CSV — your data, forever',
];

function Pricing() {
  const navigate = useNavigate();

  return (
    <section id="pricing" className="py-16 px-4" style={{ background: '#f0fafa' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: pricing copy + card */}
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-semibold mb-4 px-4 py-1.5 rounded-full"
              style={{ background: '#e6f4f3', color: '#2d8880' }}>
              <BarChart3 className="h-4 w-4" />
              Simple Pricing
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-left" style={{ color: '#1f425c' }}>
              One plan. Everything you need.
            </h2>
            <p className="text-slate-500 text-base mb-6 text-left">
              No tiers to compare. No features held back. Full access from day one. The average renovation goes <span className="font-bold text-slate-800">$12,400 over budget</span> — Stratalogy costs less than one lunch with your GC per month.
            </p>

            {/* Pricing card */}
            <div className="max-w-sm">
              <div className="relative bg-white rounded-2xl border-2 p-6 shadow-lg" style={{ borderColor: '#359b95' }}>
                <div className="absolute -top-3 left-6 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}>
                  Homeowner Plan
                </div>

                <div className="flex items-center gap-3 mb-5 mt-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#359b95' }}>
                    <Home className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Full Access</h3>
                    <p className="text-slate-500 text-xs">Everything to manage your build or renovation</p>
                  </div>
                </div>

                <div className="flex items-end gap-2 mb-1">
                  <span className="text-4xl font-extrabold text-slate-900">$39</span>
                  <span className="text-slate-400 text-base mb-1">/month</span>
                </div>
                <p className="text-slate-400 text-xs mb-4">That's $1.30/day to protect a $50K–$500K+ investment</p>

                {/* What counts as a project — inline clarification */}
                <div className="mb-5 p-3 rounded-lg text-left" style={{ background: '#f0fafa', border: '1px solid #b2dedd' }}>
                  <p className="text-xs leading-relaxed" style={{ color: '#1f425c' }}>
                    <span className="font-semibold">One project = one property.</span> Kitchen, flooring, fireplace — if it's the same house, it's all one project. You'd only add a second project for a separate property.
                  </p>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {PRICING_FEATURES.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-700 text-left">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: '#359b95' }}>
                        <CheckCircle className="h-3 w-3 text-white" />
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate('/register')}
                  className="w-full text-white font-bold py-3.5 rounded-xl text-base transition-[opacity,transform] duration-200 hover:opacity-90 hover:scale-[1.02] shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)', boxShadow: '0 4px 20px rgba(53,155,149,0.3)' }}
                >
                  Start Your 7-Day Free Trial
                </button>
                <div className="flex items-center justify-center gap-2 mt-3 p-2 rounded-lg" style={{ background: '#f0fafa' }}>
                  <Shield className="h-3.5 w-3.5 shrink-0" style={{ color: '#359b95' }} />
                  <p className="text-xs font-semibold" style={{ color: '#1f425c' }}>7-day free trial, card required. Cancel anytime before day 7 and you won't be charged.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: lifestyle image + trust signals */}
          <div className="space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-lg">
              <img
                src="/images/beautiful-kitchen-completed.png"
                alt="Beautiful completed kitchen renovation — the result of staying organized"
                loading="lazy"
                className="w-full h-auto block object-cover"
                style={{ maxHeight: '340px' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-sm font-medium drop-shadow-lg">
                  A $50K–$500K+ investment deserves more than a spreadsheet.
                </p>
              </div>
            </div>

            {/* Trust signals — horizontal */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { icon: Shield,      text: 'Secure payments via Stripe' },
                { icon: CheckCircle, text: 'Cancel any time — no lock-in' },
                { icon: CheckCircle, text: "7-day free trial, card required. Cancel anytime before day 7 and you won't be charged." },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-slate-500 text-sm text-left">
                  <Icon className="h-4 w-4 shrink-0" style={{ color: '#359b95' }} />
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

          {/* Left: header + lifestyle image */}
          <div>
            <div className="inline-flex items-center gap-2 text-sm font-semibold mb-4 px-4 py-1.5 rounded-full"
              style={{ background: '#f0fafa', color: '#2d8880' }}>
              <HelpCircle className="h-4 w-4" />
              Common Questions
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 text-left" style={{ color: '#243549' }}>
              Still on the fence? We get it.
            </h2>
            <p className="text-slate-500 text-base mb-8 text-left">
              Honest answers to the questions real homeowners ask before signing up.
            </p>

            <div className="relative rounded-2xl overflow-hidden shadow-lg hidden lg:block">
              <img
                src="/images/home-construction.jpg"
                alt="Home under construction — blueprints and planning"
                loading="lazy"
                className="w-full h-auto block object-cover"
                style={{ maxHeight: '300px' }}
              />
            </div>
          </div>

          {/* Right: FAQ accordion */}
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-semibold text-sm sm:text-base" style={{ color: '#1f425c' }}>
                      {item.q}
                    </span>
                    <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: isOpen ? '#359b95' : '#f1f5f9' }}>
                      {isOpen
                        ? <Minus className="h-4 w-4 text-white" />
                        : <Plus className="h-4 w-4 text-slate-500" />}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-slate-600 text-sm leading-relaxed text-left">
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Founder Story — side-by-side layout ──────────────────────────────────────
function FounderStory() {
  return (
    <section className="py-16 px-4" style={{ background: '#f8fafc' }}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

          {/* Left: founder info + trust signals (2/5 width) */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="h-5 w-5" style={{ color: '#359b95' }} />
              <span className="text-sm font-semibold" style={{ color: '#2d8880' }}>Why This Exists</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-left" style={{ color: '#243549' }}>
              Built by a Homeowner,<br />for Homeowners.
            </h2>

            <p className="text-slate-500 text-base mb-6 text-left">
              Not by a contractor. Not by a construction company. By someone who's been on your side of the table.
            </p>

            {/* Founder avatar */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#359b95] to-[#1f425c] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                M
              </div>
              <div>
                <p className="font-semibold text-slate-800">Michael</p>
                <p className="text-sm text-slate-500">Founder & Developer</p>
              </div>
            </div>

            {/* Trust signals */}
            <div className="space-y-3">
              {[
                { icon: Code, title: 'Solo-built', desc: 'By a developer who saw the problem firsthand' },
                { icon: MessageSquare, title: 'Direct access', desc: 'Talk to the founder, not a support queue' },
                { icon: Heart, title: 'Homeowner-first', desc: 'Every feature built from a homeowner\'s perspective' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-slate-100">
                  <div className="p-2 rounded-lg" style={{ background: '#e6f4f3' }}>
                    <Icon className="h-4 w-4" style={{ color: '#359b95' }} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-slate-800">{title}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: founder letter (3/5 width) */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
              <div className="space-y-4 text-slate-600 leading-relaxed text-left">
                <p>
                  I watched my parents spend hundreds of thousands of dollars on a home build
                  and realised there was nothing — not a single tool — designed to help the
                  <span className="font-semibold text-slate-800"> homeowner </span>
                  stay in control. Every app, every platform, every piece of software was built
                  for the contractor. Nobody was building for the people writing the checks.
                </p>
                <p>
                  I'm not a contractor. I'm not a "renovation expert." I'm a software developer who
                  saw firsthand how chaotic and overwhelming it is to manage a major build without
                  the right tools. The spreadsheets, the text threads, the lost invoices, the
                  disagreements about what was agreed to — I watched all of it happen and thought:
                  <em> there has to be a better way.</em>
                </p>
                <p className="font-semibold" style={{ color: '#1f425c' }}>
                  So I built one. Stratalogy is the tool I wish my family had from day one.
                  Every feature exists because a real homeowner needed it — and I know you will too.
                </p>
              </div>

              {/* Video placeholder */}
              <div className="mt-6 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
                <Video className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-500 mb-1">
                  Video coming soon
                </p>
                <p className="text-xs text-slate-400 max-w-md mx-auto">
                  A short message from Michael about why he built Stratalogy and what
                  makes it different from contractor tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA — side-by-side ─────────────────────────────────────────────────
function FinalCTA() {
  const navigate = useNavigate();
  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

          {/* Left: CTA content */}
          <div className="text-left">
            <img src="/stratalogy-logo-transparent.png" alt="Stratalogy" className="w-40 h-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: '#243549' }}>
              Every day without a system is another receipt lost,{' '}
              <span style={{ color: '#359b95' }}>another decision forgotten.</span>
            </h2>
            <p className="text-slate-500 text-base mb-6">
              Your renovation is too important to manage from memory. Start building your permanent project record today.
            </p>
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center gap-2 text-white font-bold px-8 py-3.5 rounded-xl text-base transition-transform duration-200 hover:scale-105 shadow-lg shadow-[#359b95]/25"
              style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}
            >
              Start your 7-day free trial
              <ArrowRight className="h-5 w-5" />
            </button>
            <p className="text-slate-400 text-sm mt-3">7-day free trial · Set up in 2 minutes · Cancel any time</p>
          </div>

          {/* Right: hero construction image */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            <img
              src="/images/hero-homeowner-blueprints.jpg"
              alt="Homeowner reviewing blueprints — take control of your renovation"
              loading="lazy"
              className="w-full h-auto block object-cover"
              style={{ maxHeight: '380px' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white text-sm font-medium drop-shadow-lg">
                Take control of your build — every dollar, every decision, organized.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact — compact inline ─────────────────────────────────────────────────
function Contact() {
  return (
    <section id="contact" className="py-12 px-4 border-t border-slate-100" style={{ background: '#f8fafc' }}>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="text-left">
            <h3 className="text-lg font-bold mb-1" style={{ color: '#243549' }}>
              Questions? Talk directly to the founder.
            </h3>
            <p className="text-slate-500 text-sm">
              No support queue. No chatbot. Michael reads and replies to every message.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="mailto:support@stratalogy.com"
              className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-white text-sm transition-opacity duration-200 hover:opacity-90 shadow-md"
              style={{ background: 'linear-gradient(135deg, #359b95, #1f425c)' }}
            >
              <Mail className="h-4 w-4" />
              support@stratalogy.com
            </a>
            <Link
              to="/help"
              className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm border-2 transition-colors duration-200 hover:bg-slate-50"
              style={{ borderColor: '#1f425c', color: '#1f425c' }}
            >
              <HelpCircle className="h-4 w-4" />
              Help Center
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <footer className="py-8 px-4 border-t border-slate-100 bg-white">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/stratalogy-logo-transparent.png" alt="Stratalogy" className="h-10 w-auto" />
          <span className="text-slate-500 text-sm">&copy; {new Date().getFullYear()} Stratalogy. All rights reserved.</span>
        </div>
        <div className="flex items-center gap-6 text-sm text-slate-400">
          <Link to="/privacy" className="hover:text-slate-600 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-slate-600 transition-colors">Terms of Service</Link>
          <Link to="/refund-policy" className="hover:text-slate-600 transition-colors">Refund Policy</Link>
          <Link to="/help" className="hover:text-slate-600 transition-colors">Help</Link>
          <a href="mailto:support@stratalogy.com" className="hover:text-slate-600 transition-colors">Contact</a>
          <Link to="/login" className="hover:text-slate-600 transition-colors">Sign In</Link>
          <Link to="/contractors" className="hover:text-slate-600 transition-colors">For Contractors</Link>
        </div>
      </div>
    </footer>
  );
}

// ─── Page — streamlined section order, no duplicates ──────────────────────────
export default function LandingPage() {
  return (
    <div className="font-sans antialiased">
      <Nav />
      <Hero />
      <PainPoints />
      <Features />
      <HowItWorks />
      <OnboardingCallout />
      <Pricing />
      <FAQ />
      <FounderStory />
      <FinalCTA />
      <Contact />
      <Footer />
    </div>
  );
}
